<template>
  <footer>
    <!-- KSK002: fixed bottom bar (120px since KSK073). Membership sign-in / member / logout
         moved to the kiosk Header (KSK003). -->
    <!-- KSK009: `footer` class is the fly-to-cart target of Order.vue's
         addCartAnimation(). -->
    <div class="ksk-bottom-bar footer" v-bind="kskSlot('cart_bar')">
      <!-- KSK064: same 96px height as the View Cart pill so the bar is balanced. -->
      <KskButton variant="outlined" icon="refresh" @click="confirmStartOver()">
        {{ $t("kiosk.footer.startOver") }}
      </KskButton>
      <div v-if="itemQuantity === 0" class="ksk-cart-empty">
        <span class="material-icons-round">shopping_bag</span>
        {{ $t("kiosk.footer.cartEmpty") }}
      </div>
      <!-- KSK064: item count + total live in the View Cart pill; the count
           badge sits on its corner (outside the button, which clips). -->
      <div class="ksk-cart-pill">
        <KskButton
          variant="primary"
          icon="shopping_bag"
          :disabled="itemQuantity === 0"
          @click="viewCart()"
        >
          {{ $t("kiosk.footer.viewCart") }}
          <template v-if="itemQuantity > 0">
            <span class="ksk-cart-pill__divider" aria-hidden="true"></span>
            <span class="ksk-cart-pill__amount">$ {{ calculateTotal() }}</span>
          </template>
        </KskButton>
        <span
          v-if="itemQuantity > 0"
          :key="itemQuantity"
          class="ksk-cart-pill__badge"
          :aria-label="$t(itemQuantity === 1 ? 'kiosk.common.itemOne' : 'kiosk.common.itemMany', { count: itemQuantity })"
        >
          {{ itemQuantity > 99 ? "99+" : itemQuantity }}
        </span>
      </div>
    </div>
    <transition name="ksk-toast">
      <div v-if="showAddedToast" class="ksk-toast" role="status">
        <span class="material-icons-round">check_circle</span>
        {{ $t("kiosk.footer.addedToCart") }}
      </div>
    </transition>
    <div class="position-fixed top-1 end-1 z-index-2 message-success">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
  </footer>
</template>

<script>
import EventBus from "vue3-eventbus";
// import { useRoute } from 'vue-router';
import { localStorageName } from "@/config/utils";
import KskButton from "./ui/KskButton.vue";
import { kskSlot } from "@/config/kioskColorSlots";
import { cartSubtotal, cartTotalPayable } from "@/config/cartTotal";
import { promoDiscountAmount } from "@/config/promoCode";
import { DINE_IN } from "@/config/const";
// import memberService from "../../../services/member.service";

export default {
  name: "KioskOrderFooter",
  components: {
    KskButton,
  },
  props: {
    confirmStartOver: {
      type: Function,
      default: () => {},
    }
  },
  data() {
    return {
      merchantCode: "",
      branchCode: "",
      branch: JSON.parse(
        localStorage.getItem(localStorageName("BRANCH_INFO"), "")
      ),
      itemQuantity: 0,
      itemSelect: [],
      memberInfo: JSON.parse(
        localStorage.getItem(localStorageName("KIOSK_MEMBER_INFO"), "")
      ),
      isLogined: false,
      cartName: "ORDER_INFO",
      snackbar: null,
      message: "",
      showAddedToast: false,
      addedToastTimer: null,
      diningOption:
        localStorage.getItem(localStorageName("DINING_OPTION")) ?? DINE_IN,
    };
  },
  async created() {
    this.cartName = await this.$store.dispatch("cartName");
    this.merchantCode = this.$store.state.merchantCode;
    this.branchCode = this.$store.state.branchCode;

    let orderInfo = JSON.parse(localStorage.getItem(this.cartName, null)) ?? [];
    this.itemSelect = orderInfo;

    orderInfo.forEach((item) => {
      this.itemQuantity += item.quantity;
    });

    if (this.memberInfo) {
      this.isLogined = true;
    }
  },
  mounted() {
    EventBus.on("AddToCartSuccess", async () => {
      setTimeout(() => this.refreshCart(), 300);
    });
    EventBus.on("ChangeItemQuantitySuccess", async () => {
      setTimeout(() => this.refreshCart(), 300);
    });
    EventBus.on("DiningOptionChanged", () => {
      this.diningOption =
        localStorage.getItem(localStorageName("DINING_OPTION")) ?? DINE_IN;
    });
    EventBus.on("LogoutSuccess", () => {
      this.memberInfo = null;
      this.isLogined = false;
    });

    EventBus.on("LoginSuccess", () => {
      this.memberInfo = JSON.parse(
        localStorage.getItem(localStorageName("KIOSK_MEMBER_INFO")) || "null"
      );
      this.isLogined = !!this.memberInfo;
    });
  },
  beforeUnmount() {
    clearTimeout(this.addedToastTimer);
  },
  methods: {
    kskSlot,
    // KSK009: re-read the cart; a higher item count means something was added,
    // whichever path added it (item sheet, "+", stepper, upsell).
    refreshCart() {
      const previous = this.itemQuantity;
      const orderInfo =
        JSON.parse(localStorage.getItem(this.cartName, null)) ?? [];
      this.itemSelect = orderInfo;
      this.itemQuantity = orderInfo.reduce((sum, item) => sum + item.quantity, 0);
      if (this.itemQuantity > previous) {
        this.showAddedToast = true;
        clearTimeout(this.addedToastTimer);
        this.addedToastTimer = setTimeout(() => {
          this.showAddedToast = false;
        }, 2000);
      }
    },

    // The pill shows the Total Payable Checkout will show (service charge, GST,
    // promo, BCRS), not just the items. A member voucher is priced by the API
    // on Checkout.
    calculateTotal() {
      let promoCode = null;
      try {
        promoCode = JSON.parse(
          localStorage.getItem(localStorageName("KIOSK_PROMO_CODE_APPLIED")) || "null"
        );
      } catch (error) {
        promoCode = null;
      }
      return cartTotalPayable({
        items: this.itemSelect,
        branch: this.branch,
        diningOption: this.diningOption,
        useMemberPrice: this.isLogined,
        promoApplied: !!promoCode,
        promoDiscount: promoCode
          ? promoDiscountAmount(
              promoCode,
              cartSubtotal(this.itemSelect, this.isLogined),
              this.itemSelect,
              this.isLogined
            )
          : 0,
      }).toFixed(2);
    },

    // Upsell items now live on Checkout as "Popular Add-Ons" (no popup).
    viewCart() {
      this.$router.push({
        name: "CheckoutPageKiosk",
        params: {
          merchant_code: this.merchantCode,
          branch_code: this.branchCode,
        },
      });
    },

    startOver() {
      localStorage.removeItem(this.cartName);
      localStorage.removeItem(localStorageName("DINING_OPTION"));

      this.$router.push({
        name: "LandingPageKiosk",
        params: {
          merchant_code: this.merchantCode,
          branch_code: this.branchCode,
        },
      });
    },

    logout() {
      EventBus.emit("Logout");
      // memberService.logout(this.merchantCode);
      // localStorage.removeItem(localStorageName("DINING_OPTION"));
      // localStorage.removeItem(localStorageName("SELECTED_REWARD"));
      // this.memberInfo = null;
      // this.isLogined = false;

      // this.$emit("logout-done");
    },

    onLogout() {
      if (window.confirm(this.$t("kiosk.common.confirmLogout"))) {
        this.logout();
      }
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
  },
};
</script>

<style scoped>
/* KSK002 — fixed bottom bar (120px since KSK073), primary action in the lower reach zone. */
.ksk-bottom-bar {
  position: fixed;
  left: 0;
  bottom: var(--ksk-powered-height);
  z-index: 2;
  width: 100%;
  height: var(--ksk-bottom-bar-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ksk-space-3);
  padding: 0 var(--ksk-page-padding);
  background: var(--ksk-surface);
  /* KSK063: cart_bar colour slot (outline = top line, font = captions,
     accent = item badge + total); theme values otherwise. The two buttons
     keep their own button slots. */
  border-top: 1px solid var(--ksk-slot-outline, var(--ksk-border));
  /* Top corners rounded like the Landing panel. */
  border-radius: var(--ksk-radius-popup) var(--ksk-radius-popup) 0 0;
  box-shadow: var(--ksk-shadow-bar);
  font-family: var(--ksk-font);
}

/* KSK040: the two actions keep their size; only the total may shrink. */
.ksk-bottom-bar > .ksk-btn {
  flex: none;
}
/* KSK064: Start Over matches the View Cart pill (96px tall, ≥320px wide). */
.ksk-bottom-bar > .ksk-btn--outlined {
  min-width: 320px;
}

/* KSK064: View Cart pill carries the total; count badge on its top-right
   corner, re-keyed on every change so the pop animation replays. */
.ksk-cart-pill {
  position: relative;
  flex: none;
}
.ksk-cart-pill__divider {
  display: inline-block;
  width: 2px;
  height: 40px;
  margin: 0 var(--ksk-space-1);
  background: currentColor;
  opacity: 0.35;
  vertical-align: middle;
}
.ksk-cart-pill__amount {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.ksk-cart-pill__badge {
  position: absolute;
  top: -12px;
  right: -8px;
  min-width: 42px;
  height: 42px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 3px solid var(--ksk-surface);
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-slot-accent, var(--ksk-error));
  color: #ffffff;
  font-family: var(--ksk-font);
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  box-shadow: var(--ksk-shadow-float);
  pointer-events: none;
  animation: ksk-badge-pop 320ms cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes ksk-badge-pop {
  0% { transform: scale(0.4); }
  60% { transform: scale(1.2); }
  100% { transform: scale(1); }
}
.ksk-cart-empty {
  display: flex;
  align-items: center;
  gap: var(--ksk-space-1);
  font-size: var(--ksk-body-size);
  line-height: var(--ksk-body-lh);
  color: var(--ksk-slot-font, var(--ksk-text-secondary));
  white-space: nowrap;
}
.ksk-cart-empty .material-icons-round {
  font-size: 32px;
}

/* KSK009 "Added to cart" toast, just above the bottom bar. */
.ksk-toast {
  position: fixed;
  left: 50%;
  bottom: calc(var(--ksk-bottom-bar-height) + var(--ksk-powered-height) + var(--ksk-space-3));
  z-index: 3;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: var(--ksk-space-1);
  height: 64px;
  padding: 0 var(--ksk-space-4) 0 var(--ksk-space-3);
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-text);
  /* KSK114: the pill is always dark, so its text stays white — --ksk-on-primary
     turns dark when the merchant picks a light brand colour. */
  color: #ffffff;
  box-shadow: var(--ksk-shadow-float);
  font-family: var(--ksk-font);
  font-size: var(--ksk-body-size);
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
}
.ksk-toast .material-icons-round {
  font-size: 28px;
  color: var(--ksk-primary-container);
}
.ksk-toast-enter-active,
.ksk-toast-leave-active {
  transition: opacity 160ms ease-out, transform 160ms ease-out;
}
.ksk-toast-enter-from,
.ksk-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 12px);
}
</style>
